import {
    questionCustomisationFilterPrefix,
    QuestionCustomisationFilterType,
} from '../../../api/models/survey';
import { AttendeeSurvey, SurveyQuestion } from '../../../models/survey';
import { isCannedQuestion, isRequiredQuestion } from '../../../utils/surveys';
import {
    CANNED_QUESTION_COMPANY,
    CANNED_QUESTION_WORK_ADDRESS,
    CHECKOUT_NATIVE_ANDROID,
    CHECKOUT_NATIVE_IOS,
    CHECKOUT_NATIVE_WEB,
} from '../constants';
import { cleanUnregisteredQuestionAnswers } from './';

export const cleanUpDuplicateTaxesQuestions = (
    questionsList: Array<Pick<SurveyQuestion, 'name'>>,
) => {
    const duplicateQuestionList: Array<string> = [
        CANNED_QUESTION_WORK_ADDRESS,
        CANNED_QUESTION_COMPANY,
    ];

    return questionsList.filter(
        (element) => !duplicateQuestionList.includes(element.name),
    );
};

export const canUseIOSmessage = () => {
    const windowWithWebkit = window as unknown as {
        webkit?: { messageHandlers?: { ffi?: unknown } };
    };

    return (
        windowWithWebkit.webkit !== undefined &&
        windowWithWebkit.webkit.messageHandlers !== undefined &&
        windowWithWebkit.webkit.messageHandlers.ffi !== undefined
    );
};

export const canUseAndroidMessage = () => window.android !== undefined;

export const getNativePlatform = () => {
    if (canUseIOSmessage()) {
        return CHECKOUT_NATIVE_IOS;
    }

    if (canUseAndroidMessage()) {
        return CHECKOUT_NATIVE_ANDROID;
    }

    return CHECKOUT_NATIVE_WEB;
};

export const sendNativeCheckoutPurchaseAction = async <Message>(
    message: Message,
    webCallback: () => void,
) => {
    switch (getNativePlatform()) {
        case CHECKOUT_NATIVE_IOS:
            return (
                window as unknown as {
                    webkit: {
                        messageHandlers: {
                            ffi: { postMessage: Window['postMessage'] };
                        };
                    };
                }
            ).webkit.messageHandlers.ffi.postMessage(message);
        case CHECKOUT_NATIVE_ANDROID:
            return window.android.postMessage(message);
        default:
            // CHECKOUT_NATIVE_WEB or others
            return webCallback();
    }
};

const isString = (variable: unknown): variable is string =>
    typeof variable === 'string';
const isObject = (variable: unknown): variable is Record<string, unknown> =>
    !!variable && typeof variable === 'object' && !Array.isArray(variable);

export const cleanFormData = (
    formData: {
        [key: string]: any;
    },
    survey: {
        questionsForBasicInfo: Array<SurveyQuestion>;
        questionsForBuyer: Array<SurveyQuestion>;
        questionsForAttendees: Array<AttendeeSurvey>;
    },
) => trimStringPropsObject(cleanUnregisteredQuestionAnswers(formData, survey));

export const trimStringPropsObject = <Data extends Record<string, unknown>>(
    data: Data,
): Data =>
    Object.fromEntries(
        Object.entries(data).map(([key, value]) => {
            if (isString(value)) {
                return [key, value.trim()];
            }

            return [
                key,
                isObject(value) ? trimStringPropsObject(value) : value,
            ];
        }),
    ) as Data;

export const getOptionalQuestions = <Question extends Array<SurveyQuestion>>(
    questions: Question,
) => questions.filter((question) => !isRequiredQuestion(question, questions));

export const getOptionalOrCannedQuestions = (
    questions: Array<SurveyQuestion>,
) =>
    questions.filter(
        (question) =>
            !isRequiredQuestion(question, questions) ||
            isCannedQuestion(question),
    );

export const getRequiredQuestions = (questions: Array<SurveyQuestion>) =>
    questions.filter((question) => isRequiredQuestion(question, questions));

export const getRequiredAttendeesQuestions = (
    questionsForAttendees: Array<AttendeeSurvey>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getRequiredQuestions(attendee.survey),
    }));

export const getOptionalAttendeesQuestions = <T extends AttendeeSurvey>(
    questionsForAttendees: Array<T>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getOptionalQuestions(attendee.survey),
    }));

export const getOptionalOrCannedAttendeesQuestions = (
    questionsForAttendees: Array<AttendeeSurvey>,
) =>
    questionsForAttendees.map((attendee) => ({
        ...attendee,
        survey: getOptionalOrCannedQuestions(attendee.survey),
    }));

export const getTotalNumberOfQuestionsByCustomisation = (
    questions: Array<SurveyQuestion>,
    questionCustomizationFilter: QuestionCustomisationFilterType,
) =>
    questions.filter(({ name }) =>
        name.startsWith(
            questionCustomisationFilterPrefix[questionCustomizationFilter],
        ),
    ).length;
